/** ***********
*
* <filter.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, race, Subscription } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { FilterService } from 'src/app/services/filter.service';
import {
    FilterFormModel,
    FilterOptions,
    FilterOption,
    MAP_FILTER_FORM_ENTRY,
    ADMIN_FAMILIES_FILTER_FORM_ENTRY,
    ADMIN_ASSETS_FORM_ENTRY,
    AIRCRAFT_PLANNING_FORM_ENTRY,
    ADMIN_DEVICES_FORM_ENTRY,
    PLANNING_FORM_ENTRY,
    REQUEST_FOR_RIGHT_FORM_ENTRY,
    MANAGE_USER_ROLES_FORM_ENTRY,
    ADMIN_ZONES_FORM_ENTRY,
    CERTIFICATES_FILTER_FORM_ENTRY,
    GROUPS_FILTER_FORM_ENTRY,
    MANAGE_FAMILY_USERS_FORM_ENTRY,
    ANCHOR_FILTER_FORM,
    QuickFilterOption,
} from 'src/app/models/filter-form.model';
import { selectProfileUserRights$, selectProfileUserRightsLoaded$ } from '../../store/selectors/profile.selectors';
import { Scope, UserRights } from 'src/app/models/profile';
import moment from 'moment';
import { KeyValue } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { TransformPropertyPipe } from 'src/app/pipes/transform-property.pipe';
import { WorkerService } from 'src/app/services/workers.service';
import { FilterStatic } from './filter.static';
import { SnackbarComponent } from '../shared/snackbar/snackbar';
import { detailExpand, rotate } from '../shared/animations';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { capitalizeFirstLetters } from 'src/app/utils/capitalizeFirstLetter';
import { Asset } from 'src/app/models/asset';
import { SignalSources } from 'src/app/models/signal-source';
import { isEqual, pick, valuesIn } from 'lodash';
import { selectAssetReady$ } from 'src/app/store/selectors/asset.selectors';
import { DialogValidationComponent } from '../shared/dialog/dialog-validation/dialog-validation.component';

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    animations: [detailExpand, rotate(180)],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FilterComponent<T = object> implements OnInit, OnDestroy {

    constructor(
        private filterService: FilterService,
        private _workerService: WorkerService,
        private store: Store,
        public translate: TranslateService,
        private _router: Router,
        private _route: ActivatedRoute,
        private _translate: TranslateService,
        private _transformPropertyPipe: TransformPropertyPipe,
        private _snackbar: SnackbarComponent,
        private _dialog: MatDialog,
        private _cd: ChangeDetectorRef,
    ) {
        this._userRights$ = this.store.select(selectProfileUserRights$);
        this._userRightsLoaded$ = this.store.select(selectProfileUserRightsLoaded$);
    }

    @Input() parentSourceView: string;
    @Input() dataForFilter$: Observable<T[]>; // It's an array of any cause it can have multiple model sources (assets, devices, families...)
    @Input()
    get hideCollapseButton() {
        return this._hideCollapseButton;
    }
    set hideCollapseButton(value: BooleanInput) {
        this._hideCollapseButton = coerceBooleanProperty(value);
    }
    @Output() closeFilterSidebar = new EventEmitter<boolean>();
    @Output() closeFilterPanel = new EventEmitter<boolean>();
    @Output() clickSubmit = new EventEmitter(); // emit on click on apply filter button

    public isFormInitialised = false;
    public ranges = [
        { label: '+/- 1 ' + this.translate.instant('WEEKS'), value: 'weeks' },
        { label: '+/- 1 ' + this.translate.instant('MONTHS'), value: 'months' },
        { label: this.translate.instant('HISTORY_RANGE_CUSTOM'), value: 'custom' }];
    public displayCustomRange = false;
    public rangeControl: string;
    public showFilterPanel = true;
    public displayFiltersPanel = true;
    public datesFilterForm: FormGroup<{
        [key: string]: FormGroup<{
            start: FormControl<any>;
            end: FormControl<any>;
        }>;
    }>;
    public dynamicFilterForm: FormGroup<{
        [key: string]: FormControl<any>;
    }>; // Represent the form with all fields to filter
    public filterFormEntries: FilterFormModel[]; // Contain the metadata of the component filter
    public filterFormEntriesNotFiltered: FilterFormModel[]; // Contain the metadata of the component filter
    public filteredDataForAutocomplete: { [key: string]: Observable<FilterOption[]>; } = {}; // Contain the result of a value change (partial data because filtered)

    /**
      * The filter options selection in progress, not applied to the data
      */
    public filterOptionsTmp: FilterOptions = {}; // Values in the filter fields
    /**
     * the filter options appied to the data
     */
    public filterOptions: FilterOptions = {};
    public searchFieldControls: FormGroup<{ [control: string]: FormControl<string> }>; // controls bound to each mat-select-search search fields
    public filterLoader: boolean;
    public filterAutocompleteLoaders: { [key: string]: boolean; } = {};

    private _dataSource: T[];
    private _dataSourceFiltered: T[];
    private _dataForAutocomplete: { [key: string]: FilterOption[]; } = {}; // Autocomplete data at initialisation (full data)
    private _userRights$: Observable<UserRights>; // For User right
    private _userRightsLoaded$: Observable<boolean>; // For User right
    private _scope: string[];
    public hasClickedApplyFilterButton = false;
    /**
     * Boolean set to false if there are some filter parameters in the URL
     * in order to not apply default filters value during initialization in this case
     */
    private _isDefaultValueToApply = true;
    /**
     * Object of subjects which emit when autocomplete data are ready for the corresponding filter form entry
     */
    private autoCompleteSubjects: { [key: string]: BehaviorSubject<boolean>; } = {};
    private subscription: Subscription = new Subscription();
    private readonly MAX_LENGTH_FILTER_DATA_AUTOCOMPLETE = 200;
    private _hideCollapseButton = false;
    private _isThisFilterApplied = false;

    ngOnInit(): void {
        this.subscription.add(this.filterService.hasClickedApplyButton$.subscribe((hasClickedApplyFilterButton) => {
            this.hasClickedApplyFilterButton = hasClickedApplyFilterButton;
        }));

        /**
         * Update filters if Quick Search Filter applied
         */
        this.subscription.add(
            this.filterService.quickFilterApplied$.subscribe((quickSearch) => {
                if (quickSearch) {
                    this._confirmClearFilters(quickSearch);
                }
            })
        );

        /**
         * Clear filters if Quick Search Filter cleared
         */
        this.subscription.add(
            this.filterService.quickFilterClear$.subscribe(() => {
                this.resetFilters();
                this.clickSubmit.emit();
            })
        );

        /**
         * Reset filter if Context changed
         */
        this.subscription.add(this.store.select(selectAssetReady$).pipe(
            filter((ready) => ready)
        ).subscribe(() => {
            if (this.dynamicFilterForm) {
                this.resetFilters(true);
                this.clickSubmit.emit();
            }
        }));

        this._initWorker();
        this._handleUrlSpecificDisplay();
        this._setFilterModel();
        this._parseQueryParams().pipe(take(1)).subscribe(() => {
            this._initData();
            this._cd.markForCheck();
        });
    }

    /**
     * Listen to the form group changes and pre-filter the data source accordingly
     * @param entry FilterModel of datePicker type
     * @param formGroup The form group containing start and end form control
     */
    public onDatePickerChanges(entry: FilterFormModel) {
        const value = this.dynamicFilterForm.controls[entry.name].value as { start: moment.Moment, end: moment.Moment; };
        if (value.start && value.end) { // If both start and end form control are filled
            this.filterOptionsTmp[entry.name] = [{ key: 'start', value: value.start.startOf('day').toISOString() }, { key: 'end', value: value.end.endOf('day').toISOString() }];
            this._setAutocompleteList(entry);
        }
        if (!value.start && !value.end) {
            this.rangeControl = null;
        }
    }

    /**
     * Triggered when the mat-select is opening
     */
    public onOpenSelect(isOpen: boolean, entry: FilterFormModel) {
        this.filterAutocompleteLoaders[entry.name] = true;
        this.autoCompleteSubjects[entry.name].next(false);

        setTimeout(() => {
            if (isOpen) {
                this._setAutocompleteList(entry);
            }
        }, 200);
    }

    /**
     * Set the autocomplete list (select options) for the given entry
     */
    private _setAutocompleteList(entry: FilterFormModel) {
        this._dataForAutocomplete[entry.name] = FilterStatic.getFilterOptions(entry, this._dataSource, this.filterFormEntriesNotFiltered, this.filterOptionsTmp);
        this.filteredDataForAutocomplete[entry.name] = this.searchFieldControls.controls[entry.name].valueChanges.pipe(
            startWith(this.searchFieldControls.controls[entry.name].value),
            map(value => this._filterDataForAutocomplete(value, entry, this._dataForAutocomplete[entry.name]))
        );
        this.filterAutocompleteLoaders[entry.name] = false;
        // emit autocomplete subject
        this.autoCompleteSubjects[entry.name].next(true);

    }

    /**
     * Triggered when a value is selected in a DD list
     * @param entry Filter form model containing all properties of the selected filter field
     * @param inputValue Value selected in the DD list
     */
    public selected(entry: FilterFormModel, event: MatSelectChange): void {
        setTimeout(() => {
            const values: Array<{ key: string, value: string, scope?: Scope; }> = event.value;
            // format and add the new elements
            this.filterOptionsTmp[entry.name] = values.map((inputValue) => {
                const value = inputValue?.value.toString().split(' | ')[0];
                const key = inputValue?.key.toString().split(' | ')[0];
                return {
                    key,
                    value: inputValue.scope ? this.transformOption({ key, value, scope: inputValue.scope }, entry) : value,
                };
            });

            this.dynamicFilterForm.controls[entry.name].setValue(this.filterOptionsTmp[entry.name]);
            // Clean below elements
            this.filterFormEntries?.forEach((existingEntry: FilterFormModel) => {
                if (existingEntry.hierarchyOrder > entry.hierarchyOrder) {
                    this.filterOptionsTmp[existingEntry.name] = [];
                    this.dynamicFilterForm.controls[existingEntry.name].setValue([]);
                    if (existingEntry.type === 'datePicker') {
                        this.dynamicFilterForm.controls[existingEntry.name].setValue({ start: '', end: '' });
                    }
                    this._cd.markForCheck();
                }
            });
        }, 0);

    }

    /**
     * Set the different range values of the start / end filters date
     * @param selectRangeControlValue Key value of the mat select range form control
     * @param entry Filter form model containing all properties of the selected filter field
     */
    public setRange(selectRangeControlValue: 'custom'|moment.unitOfTime.DurationConstructor, entry: FilterFormModel) {
        this.filterOptionsTmp[entry.name] = this.filterOptionsTmp[entry.name] || []; // initialize the filter options array item in case it is not yet done
        this.filterOptionsTmp[entry.name][2] = { key: selectRangeControlValue, value: selectRangeControlValue };
        if (selectRangeControlValue === 'custom') {
            this.displayCustomRange = true;
        } else {
            this.displayCustomRange = false;
            this.dynamicFilterForm.controls[entry.name]['controls'].start.setValue(moment().subtract(1, selectRangeControlValue).startOf('day'));
            this.dynamicFilterForm.controls[entry.name]['controls'].end.setValue(moment().add(1, selectRangeControlValue).endOf('day'));
        }
        this.onDatePickerChanges(entry);
    }

    /**
     * Fired when the user click on the submit button
     * Copy the filterOptionTmp to filter option
     */
    public onClickSubmit() {
        this._isThisFilterApplied = true;
        this.filterService.hasClickedApplyButton$.next(true);
        this.filterOptions = structuredClone(this.filterOptionsTmp);
        this._filterDataSource();
    }

    public submitFilter(): void {
        this.filterService.applyFilter(this.parentSourceView, this._dataSourceFiltered || this._dataSource);
        this.filterService.closeFilterSidebar.emit(true);
        this._appendQueryParams();
    }


    /**
     * Reset Filter options and results
     */
    public resetFilters(isContextChanged = false): void {
        this.filterService.hasClickedApplyButton$.next(true);

        this.rangeControl = null;

        this.dynamicFilterForm.reset();
        // Remove all sections
        for (const key of Object.keys(this.filterOptionsTmp)) {
            if (isContextChanged && key == 'assetStatus') {
                const assetOption = { key: 'inService', value: 'inService' };
                this.filterOptionsTmp[key] = [assetOption];
                this.filterOptions[key] = [assetOption];
            } else {
                this.filterOptionsTmp[key] = [];
                this.filterOptions[key] = [];
            }
        }

        // Reinitialise the filtered data source
        this._filterDataSource();
        // Remove filters for the current view
        this.filterService.clearFilter(this.parentSourceView, this._dataSource);
        this._clearQueryParams();
        this._cd.markForCheck();
    }

    /**
     * Function to auto select options at the initialization (compareWith attribute for mat-select multi)
     */
    public selectOptionCompare(option: FilterOption, selected: FilterOption): boolean {
        return option && selected && option.key?.toString().split(' | ')[0] === selected.key?.toString().split(' | ')[0];
    }

    /**
     * On click on display icon button, display or hide the filters panel
     */
    public displayOrHideFiltersPanel() {
        this.displayFiltersPanel = !this.displayFiltersPanel;
    }

    public isMapView() {
        return this._router.url.includes('view-map');
    }

    /**
    * tranform option value for display purpose
    */
    public transformOption(option: FilterOption, entry: FilterFormModel) {
        // let result: string;
        if (entry.translationRequired) {
            let value = option.value;
            switch (entry.translationKeyTransform) {
                case 'lowercase':
                    value = value.toLowerCase();
                    break;
                case 'titlecase':
                    value = capitalizeFirstLetters(value, /[_-\s]/);
                    break;
                case 'uppercase':
                    value = value.toUpperCase();
                    break;
            }
            return this._translate.instant(value || '');
        } else if (entry.transformRequired) {
            const lastIndex = entry.keyPath.length - 1;
            // get the name of the property based on the key path
            const propertyKey = entry.keyPath[lastIndex][entry.keyPath[lastIndex].length - 1];
            return this._transformPropertyPipe.transform(option.value, propertyKey, option.scope);
        } else { return option.value; }

    }

    /**
     * TrackBy `name` for ngFor
     * @returns 
     */
    public trackByName(index: number, item: any): string {
        return item.name;
    }

    /**
     * TrackBy `key` for ngFor
     * @returns 
     */
    public trackByKey(index: number, item: any): string {
        return item.key;
    }

    /**
    * Init the data source and autocomplete list
    */
    private _initData() {
        // Check that the user rights are correctly loaded to avoid multiple subscribe calls
        this.subscription.add(
            this._userRightsLoaded$.pipe(
                filter(loaded => loaded),
                take(1),
                switchMap(() => combineLatest([
                    this.dataForFilter$.pipe(
                        shareReplay(1),
                    ),
                    this._userRights$.pipe(
                        take(1),
                    ),
                ])),
            ).subscribe(([dataset, userRights]) => {
                this._scope = userRights.scope;
                this._dataSource = [];
                // The asset data source need to be filtered directly for Map and Tab view
                if (this.parentSourceView === 'mapView' || this.parentSourceView === 'tabView' || this.parentSourceView === 'zonesTabView') {
                    for (const asset of dataset as Asset[]) {
                        // Check if the asset is available for the map
                        if (this._checkTakeAssetForMap(asset)) {
                            this._dataSource.push(asset as T);
                        }
                    }
                } else {
                    this._dataSource = [...dataset];
                }
                // Check if the form has already been initialized to avoid multiple useless form re-build
                if (!this.isFormInitialised) {
                    this._initForm();
                    this._setFilterOptionsValuesFromKeys();
                    this._setFilterOptions();
                }
                this._filterDataSource();
            }));
    }

    /**
     * Check if the asset has to be selected for the filters on the map related components
     * @param asset The asset to check
     */
    private _checkTakeAssetForMap(asset: Asset): boolean {
        // Check last event has at least key
        if (!Object.keys(asset?.last_event ?? {}).length) {
            return false;
        }
        // Check that these properties have all a truthy value
        const propertiesNeedsTruthy = pick(asset.last_event, ['source', 'lng', 'lat', 'event_time']);
        if (valuesIn(propertiesNeedsTruthy).some(
            value => !value
        )) {
            return false;
        }
        /*
        Check for dual-tag
        Tracker is not defined, but the asset is taken into account
        */
        if (propertiesNeedsTruthy.source === SignalSources.GO2SCAN) {
            return true;
        }
        const { tracker } = asset;
        // Tracker needs to be defined
        return !!Object.keys(tracker ?? {}).length;
    }

    /**
     * Init Worker
     */
    private _initWorker() {
        this._workerService.filters.onmessage = ({ data }: MessageEvent) => {
            if (data.hasClickedApplyFilterButton) {
                // set back this observable boolean to false
                this.filterService.hasClickedApplyButton$.next(false);
            }
            this.filterService.workerBusy$.next(false);
            this._handleFilterState(data);
            this._cd.markForCheck();
        };
    }

    /**
     * Handle filtration state
     */
    private _handleFilterState(data: { dataSourceFiltered: any[]; }) {
        this._dataSourceFiltered = data.dataSourceFiltered;
        this.submitFilter();
    }

    /**
     * Get all selected options
     * @param entry FilterFormModel entry
     * @returns List of selected options
     */
    private _getSelectedOptions(entry: FilterFormModel, data: FilterOption[]): FilterOption[] {
        const selectedOptions: FilterOption[] = [];
        if (this.filterOptionsTmp[entry.name]) {
            this.filterOptionsTmp[entry.name].forEach((option) => {
                const selectedOption = data.find(item => item.key === option.key);
                if (selectedOption) {
                    selectedOptions.push(selectedOption);
                }
            });
        }
        return selectedOptions;
    }

    /**
     * Subscribe to the router handler and force the filter panel display on map view
     */
    private _handleUrlSpecificDisplay() {
        this.subscription.add(this._router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            if (event.urlAfterRedirects === '/map/view-map') {
                this.displayFiltersPanel = true;
            }
        }));
    }

    /**
     * Parse the URL query parameters in order to get search keys fields and then set & apply the filters
     */
    private _parseQueryParams(): Observable<void> {
        return new Observable((observer) => {
            // get URL query parameters
            const params = this._route.snapshot.queryParams;
            // Do not apply default values if there are some queryparameters in the URL
            if (Object.keys(params).length) {
                this._isDefaultValueToApply = false;
            }

            for (const param of Object.keys(params)) {
                // check if the params corresponds to one of the filter form entries (for multi filters screens)
                if (this.filterFormEntriesNotFiltered.some(entry => entry.name === param)) {
                    // for dates, build an array of strings
                    if (this.filterFormEntriesNotFiltered.find(entry => entry.name === param).type === 'datePicker') {
                        this.filterOptionsTmp[param] = [...params[param]];
                        continue;
                    }
                    // build key value object (only the key is available for the moment )
                    this.filterOptionsTmp[param] = Array.isArray(params[param]) ?
                        params[param]?.map(value => ({ key: value, value })) :
                        [{ key: params[param], value: params[param] }];

                }
            }

            this._parseQuickFilterPrefillFromQueryParams();
            observer.next();
            observer.complete();
        });
    }

    /**
     * Parses the options from the query parameters to determine if a quick filter prefill should be triggered.
     * This is typically used to prefill the quick filter input based on the current query parameters
     */
    private _parseQuickFilterPrefillFromQueryParams() {
        if (Object.keys(this.filterOptionsTmp).length === 1 && this.filterOptionsTmp[Object.keys(this.filterOptionsTmp)[0]].length === 1 && Object.keys(this.filterOptionsTmp)[0] !== 'assetStatus') {
            const prefillKey = this.filterOptionsTmp[Object.keys(this.filterOptionsTmp)?.[0]]?.[0]?.key;
            this.filterService.quickFilterPrefillQueryParams$.next(prefillKey);
        }
    }

    /**
     * Set the filter model regarding the view
     */
    private _setFilterModel() {
        switch (this.parentSourceView) {
            case 'mapView': this.filterFormEntriesNotFiltered = MAP_FILTER_FORM_ENTRY; break;
            case 'tabView': this.filterFormEntriesNotFiltered = MAP_FILTER_FORM_ENTRY; break;
            case 'zonesTabView': this.filterFormEntriesNotFiltered = MAP_FILTER_FORM_ENTRY; break;
            case 'assetsView': this.filterFormEntriesNotFiltered = ADMIN_ASSETS_FORM_ENTRY; break;
            case 'familiesView': this.filterFormEntriesNotFiltered = ADMIN_FAMILIES_FILTER_FORM_ENTRY; break;
            case 'devicesView': this.filterFormEntriesNotFiltered = ADMIN_DEVICES_FORM_ENTRY; break;
            case 'zonesView': this.filterFormEntriesNotFiltered = ADMIN_ZONES_FORM_ENTRY; break;
            case 'planningView': this.filterFormEntriesNotFiltered = PLANNING_FORM_ENTRY; break;
            case 'aircraftPlanningView': this.filterFormEntriesNotFiltered = AIRCRAFT_PLANNING_FORM_ENTRY; break;
            case 'requestForRightsView': this.filterFormEntriesNotFiltered = REQUEST_FOR_RIGHT_FORM_ENTRY; break;
            case 'manageUserRoleView': this.filterFormEntriesNotFiltered = MANAGE_USER_ROLES_FORM_ENTRY; break;
            case 'certificatesView': this.filterFormEntriesNotFiltered = CERTIFICATES_FILTER_FORM_ENTRY; break;
            case 'manageFamilyUsersView': this.filterFormEntriesNotFiltered = MANAGE_FAMILY_USERS_FORM_ENTRY; break;
            case 'manageGroups': this.filterFormEntriesNotFiltered = GROUPS_FILTER_FORM_ENTRY; break;
            case 'anchorView': this.filterFormEntriesNotFiltered = ANCHOR_FILTER_FORM; break;
        }
        this._cd.markForCheck();
    }

    /**
     * Build the filter form following the filterFormEntry model
     */
    private _initForm(): void {
        const formGroupOptions: { [key: string]: FormControl<any>; } = {};
        const datesFilterOptions: { [key: string ]: FormGroup<{
            start: FormControl<any>;
            end: FormControl<any>;
        }>; } = {};
        this.searchFieldControls = new FormGroup({});
        this.filterFormEntries = this.filterFormEntriesNotFiltered.filter(entry => {
            // If a filter is specific to an app, check if the user already has the right to see it
            if (entry.scope && !entry.scope.some(val => this._scope?.some(auth => auth === val))) {
                return false;
            }
            return true;
        });

        this.filterFormEntries.forEach((entry) => {
            let defaultValues: FilterOption[];
            // Default value management
            if (this._isDefaultValueToApply) { // get default value only if there are not query parameters in the URL
                if (entry.value && entry.type !== 'datePicker') {
                    defaultValues = this._manageDefaultValue(entry);
                } else {
                    defaultValues = [{ key: entry.value, value: entry.value }];
                }
            }
            if (entry.type !== 'datePicker') {
                formGroupOptions[entry.name] = entry.required ? new FormControl(defaultValues?.map(({ value }) => value) || [], Validators.required)
                    : new FormControl(defaultValues?.map(({ value }) => value) || []);
                this.searchFieldControls.addControl(entry.name, new FormControl());
            } else if (entry.type === 'datePicker') {
                // Init form group for date picker with default value from URL parameters or from filterFormModel file
                const startValue = (this.filterOptionsTmp[entry.name] && this.filterOptionsTmp[entry.name][0] && moment((this.filterOptionsTmp[entry.name])[0].value).startOf('day'))
                    || (this._isDefaultValueToApply && entry.value ? moment().subtract(1, entry.value).startOf('day') : '');
                const endValue = (this.filterOptionsTmp[entry.name] && this.filterOptionsTmp[entry.name][1] && moment((this.filterOptionsTmp[entry.name])[1].value).endOf('day'))
                    || (this._isDefaultValueToApply && entry.value ? moment().add(1, entry.value).endOf('day') : '');
                const rangeValue = (this.filterOptionsTmp[entry.name] && this.rangeControl)
                    || (this._isDefaultValueToApply ? entry.value : null);
                // display the custom range field in case of rangeValue is set to custom
                this.displayCustomRange = rangeValue === 'custom' ? true : false;

                formGroupOptions[entry.name] = new FormControl({
                    start: startValue,
                    end: endValue
                });
                datesFilterOptions[entry.name] = new FormGroup({
                    start: new FormControl(startValue),
                    end: new FormControl(endValue)
                });
                this.subscription.add(
                    datesFilterOptions[entry.name].valueChanges.subscribe(
                        value => formGroupOptions[entry.name].setValue(value)
                    )
                );

                this.rangeControl = rangeValue;

                // set values into filter options array
                const value = formGroupOptions[entry.name].value;
                this.filterOptionsTmp[entry.name] = (value.start && value.end && this.rangeControl) ? [
                    value.start.startOf ? value.start.startOf('day').toISOString() : value.start,
                    value.end.endOf ? value.end.endOf('day').toISOString() : value.end,
                    this.rangeControl
                ] : undefined;
            }
            // return true;
        });
        this.dynamicFilterForm = new FormGroup(formGroupOptions);
        this.datesFilterForm = new FormGroup(datesFilterOptions);
        this.filterFormEntries.forEach((entry) => {
            if (entry.type === 'autoComplete') {
                // init multiselect controls with empty array
                this.dynamicFilterForm.controls[entry.name].setValue([]);
            } else if (entry.type === 'datePicker') {
                this.dynamicFilterForm.controls[entry.name].setValue({ start: '', end: '' });
            }
        });
        this.isFormInitialised = true;
        this.filterOptions = structuredClone(this.filterOptionsTmp);
        this._cd.markForCheck();
    }

    /**
     * Retrieve the values for each keys from the filterOptionsTmp populated from URL parameters
     */
    private _setFilterOptionsValuesFromKeys() {
        /* Init the auto complete ready subject for each filter form entries.
        All subject inside this array will emit a true value and complete when the autocomplete lists has been calculated
        Allows to know when the process is completed as this process is asynchronous */
        this.filterFormEntries.forEach((entry) => {
            this.autoCompleteSubjects[entry.name] = new BehaviorSubject<boolean>(false);
            // When data is available, retrieve the value from the key initialy stored in URL query params
            this.subscription.add(this.autoCompleteSubjects[entry.name].pipe(filter(isReady => isReady)).subscribe({
                next: () => {
                    if (this.filterOptionsTmp[entry.name]) {
                        const options = this.filterOptionsTmp[entry.name];
                        options.forEach((keyValue) => {
                            // if it is a date, do not modify it
                            if (entry.type === 'datePicker') {
                                return;
                            }
                            // if the item is a string representing a key, retrieve the value an build the key value object
                            // if no value is found in `_dataForAutocomplete`, set the keyValue to prevent issues with translate service
                            keyValue.value = this._dataForAutocomplete[entry.name]?.find((item) => item.key.toString() === keyValue.key.toString())?.value?.split(' | ')[0] ?? keyValue?.key;
                        });
                        // set the value into the select (the spread operator is user to change the ref and execute the pure pipe for tooltip)
                        this.dynamicFilterForm.controls[entry.name].setValue([...options]);
                    }
                    this._cd.markForCheck();
                }
            }));
        });
    }

    /**
     * Fill all dropdown list with the data source
     * @param selectedFilterHierarchy The hierarchy of the filter field where the user selected a value
     */
    private _filterDataSource() {
        this.filterService.workerBusy$.next(true);
        this._workerService.filters.postMessage({
            filterFormEntries: this.filterFormEntries,
            dataSource: this._dataSource,
            filterOptions: this.filterOptions,
            hasClickedApplyFilterButton: this.hasClickedApplyFilterButton
        });
    }

    /**
     * Set the filter options for field that have at least one selected value
     * Aims to get the data and prefill the mat-select
     */
    private _setFilterOptions() {
        this.filterFormEntries.forEach((entry) => {
            if (this.filterOptionsTmp[entry.name]) {
                this._setAutocompleteList(entry);
            }
        });
        this._parseQueryParams().pipe(
            take(1),
        ).subscribe(
            () => {
                if (this.filterFormEntriesNotFiltered.filter(
                    ({ value, scope }) => !!scope || (value !== undefined && value !== null)
                ).filter(
                    entry => !this.filterFormEntries.find(
                        filteredEntry => filteredEntry === entry
                    )
                ).some(
                    ({ name }) => this.filterOptionsTmp[name] !== undefined && this.filterOptionsTmp[name] !== null
                )) {
                    this._snackbar.open(this.translate.instant('FILTERS_IGNORED'), 'orange-snackbar');
                }
            }
        );
    }

    /**
     * Filter the data in the autocomplete drop down list with the value entered by the user
     * @param value Value entered by the user. Could be a String or a string[] depending if its initialisation/autocomplete value, or if there is a selected filter
     * @param entryName Entry name filter of auto complete
     */
    private _filterDataForAutocomplete(value: string, entry: FilterFormModel, data: FilterOption[]): KeyValue<string, string>[] {
        const filterValue = !value ? '' : value.toString().toLowerCase();
        // Only authorized 200 values max to display on the list
        const filteredOptions = data
            .filter((datum) => datum?.value.toString().toLowerCase().includes(filterValue))
            .sort((a, b) => a.value.localeCompare(b.value))
            .slice(0, this.MAX_LENGTH_FILTER_DATA_AUTOCOMPLETE);

        if (filterValue !== '') {
            // display filtered options normaly
            return filteredOptions;
        } else {
            /* when nothing is filled in the search field, display selected option to the top
              As the options list is limited to MAX_LENGTH_FILTER_DATA_AUTOCOMPLETE rows, we add
              selected options here.
              It is important to have selected options inside the option list in order to make
              the ngx-mat-select-search component working properly
             */
            const selectedOptions = this._getSelectedOptions(entry, data);
            // remove selected option if they are already in the list in order to avoid duplicated line
            selectedOptions.forEach((option) => {
                const index = filteredOptions.indexOf(option);
                if (index > -1) {
                    filteredOptions.splice(index, 1);
                }
            });
            // add selected options to the top of the list
            filteredOptions.unshift(...selectedOptions);

            return filteredOptions;
        }
    }

    /**
     * Manage the default value of a filter field
     * @entry Filter field entry
     */
    private _manageDefaultValue(entry: FilterFormModel): FilterOption[] {
        const defaultValues: FilterOption[] = [];
        if (entry.value instanceof Array) {
            defaultValues.push(...entry.value.map(
                value => ({ key: value, value: value })
            ));
        } else {
            defaultValues.push({ key: entry.value, value: entry.value });
        }
        const options = this.filterOptionsTmp[entry.name];
        if (options) { // If the default value is already in no need to add it
            defaultValues.forEach(defaultValue => {
                if (!options.some((filterEl) => isEqual(defaultValue, filterEl))) {
                    options.push(defaultValue);
                }
            });
        } else {
            this.filterOptionsTmp[entry.name] = [];
            (this.filterOptionsTmp[entry.name]).push(...defaultValues);
        }
        return defaultValues;
    }



    /**
     * Add query parameter without triggering the navigation
     */
    private _appendQueryParams() {
        const params = {};
        // Build the queryParams array
        for (const item of Object.keys(this.filterOptions)) {
            const value = this.filterFormEntriesNotFiltered.find(entry => entry.name === item).type === 'datePicker' ?
                this.filterOptions[item] : // for date picker, keep the full date string
                (this.filterOptions[item])?.map((keyValue) => keyValue.key); // for others, keep only keys in order not to overload the URL
            if (this.filterOptions[item]?.length !== 0) {
                params[item] = value;
            } else {
                params[item] = undefined;
            }
        }

        /** Append new params into url */
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
                ...params
            },
            queryParamsHandling: 'merge',
        });
    }

    /**
     * Remove query param corresponding to the filter
     * - It is done by setting parameters values to undefined
     */
    private _clearQueryParams() {
        const params = {};
        for (const param of Object.keys(this._route.snapshot.queryParams)) {
            if (this.filterFormEntriesNotFiltered.some(entry => entry.name === param)) {
                params[param] = undefined;
            }
        }

        // Remove params
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
                ...params
            },
            queryParamsHandling: 'merge',
        });
    }
    
    /**
     * Checks if any filter values (except 'assetStatus') exist in the dynamic filter form.
     * If filter values are present, opens a confirmation dialog to ask the user before clearing filters.
     * If the user confirms, or if no filter values exist, applies the selected quick filter.
     * @param quickSearch - The quick filter option to apply after confirmation or if no filters are set.
     */
    private async _confirmClearFilters(quickSearch: QuickFilterOption) {
        let valueExists = false;
        
        if (this._isThisFilterApplied) { // if Apply is clicked in Filter component, execute the below code
            for (const [key, control] of Object.entries(this.dynamicFilterForm.controls)) {
                if (key !== 'assetStatus' && control.value && control.value.length > 0) {
                    valueExists = true;
                    break;
                }
            }
        }

        if (valueExists) {
            const dialogRef: MatDialogRef<DialogValidationComponent> = this._dialog.open(DialogValidationComponent, {
                minWidth: '250px',
                data: {
                    title: this._translate.instant('CLEAR_FILTER'),
                    textContent: this._translate.instant('CLEAR_FILTER_DESCRIPTION'),
                    validateText: this._translate.instant('DIALOG_YES_OPTION'),
                    cancelText: this._translate.instant('DIALOG_NO_OPTION'),
                    dangerousAction: true
                }
            });

            if (await this._confirmClear(dialogRef)) {
                this._assignQuickFilter(quickSearch);
                this._isThisFilterApplied = false;
            } else {
                this._isThisFilterApplied = true;
            }
        } else {
            this._assignQuickFilter(quickSearch);
        }
    }

    /**
     * Opens a confirmation dialog and waits for the user's response.
     * The dialog is closed automatically upon validation to ensure the event stream is not completed prematurely.
     */
    private async _confirmClear(dialogRef: MatDialogRef<DialogValidationComponent>): Promise<boolean> {
        return await firstValueFrom(race(
            dialogRef.componentInstance.onValidateEvent.pipe(
                map(() => true),
                // Need to use close to do not complete the onValidateEvent before closing the dialogRef
                tap(() => dialogRef.close()),
            ),
            dialogRef.afterClosed().pipe(
                map(() => false),
            ),
        ), { defaultValue: false });
    }

    /**
     * Applies a quick filter option to the filter form.
     * This method resets all filters. 
     * The autocomplete list is updated, and the filter form is submitted after a short delay.
     * @param quickSearch - The quick filter option to apply.
     */
    private _assignQuickFilter(quickSearch: QuickFilterOption) {
        this.rangeControl = null;
        this.dynamicFilterForm.reset();
        // Remove all sections
        for (const key of Object.keys(this.filterOptionsTmp)) {
            this.filterOptionsTmp[key] = [];
            this.filterOptions[key] = [];
        }

        // Remove filters for the current view
        this.filterService.clearFilter(this.parentSourceView, this._dataSource);
        this._clearQueryParams();

        const value = quickSearch?.value.toString().split(' | ')[0];
        const key = quickSearch?.key.toString().split(' | ')[0];
        this.filterOptionsTmp[quickSearch.name] = [{
            key,
            value: quickSearch.scope ? this.transformOption({ key, value, scope: quickSearch.scope }, quickSearch) : value,
        }];

        this._setAutocompleteList(quickSearch);
        this.dynamicFilterForm.controls[quickSearch.name].setValue(this.filterOptionsTmp[quickSearch.name]);

        // Submit
        this.filterService.hasClickedApplyButton$.next(true);
        this.filterOptions = structuredClone(this.filterOptionsTmp);
        this._filterDataSource();
        this.clickSubmit.emit();
        this._cd.detectChanges();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
